import { type FC } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type {
    ConditionOperator,
    LogicalOperator,
    RuleCondition,
    TargetRule,
    TargetableField,
} from '@/types/feature-flag'

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
    equals: 'is',
    not_equals: 'is not',
    gt: 'is greater than',
    gte: 'is at least',
    lt: 'is less than',
    lte: 'is at most',
    contains: 'contains',
    not_contains: 'does not contain',
    starts_with: 'starts with',
    ends_with: 'ends with',
    in: 'is one of',
    not_in: 'is not one of',
    exists: 'is set',
    not_exists: 'is not set',
    before: 'is before',
    after: 'is after',
    between: 'is between',
}

/** Operators that compare against nothing at all. */
const VALUELESS_OPERATORS: ConditionOperator[] = ['exists', 'not_exists']

/** Operators that take a list rather than a single value. */
const LIST_OPERATORS: ConditionOperator[] = ['in', 'not_in', 'between']

interface TargetingRuleBuilderProps {
    rules: TargetRule[]
    onChange: (rules: TargetRule[]) => void
    ruleOperator: LogicalOperator
    onRuleOperatorChange: (operator: LogicalOperator) => void
    fields: TargetableField[]
    disabled?: boolean
}

const emptyCondition = (fields: TargetableField[]): RuleCondition => ({
    field: fields[0]?.field ?? '',
    operator: fields[0]?.operators?.[0] ?? 'equals',
    value: '',
})

export const TargetingRuleBuilder: FC<TargetingRuleBuilderProps> = ({
    rules,
    onChange,
    ruleOperator,
    onRuleOperatorChange,
    fields,
    disabled = false,
}) => {
    const fieldMap = new Map(fields.map(field => [field.field, field]))

    const updateRule = (index: number, next: Partial<TargetRule>) => {
        onChange(
            rules.map((rule, i) => (i === index ? { ...rule, ...next } : rule))
        )
    }

    const updateCondition = (
        ruleIndex: number,
        conditionIndex: number,
        next: Partial<RuleCondition>
    ) => {
        const rule = rules[ruleIndex]
        updateRule(ruleIndex, {
            conditions: rule.conditions.map((condition, i) =>
                i === conditionIndex ? { ...condition, ...next } : condition
            ),
        })
    }

    /**
     * Changing the field can invalidate the operator (a date field can't do
     * "contains"), so fall back to the new field's first legal operator.
     */
    const handleFieldChange = (
        ruleIndex: number,
        conditionIndex: number,
        fieldKey: string
    ) => {
        const field = fieldMap.get(fieldKey)
        const current = rules[ruleIndex].conditions[conditionIndex]
        const operator = field?.operators.includes(current.operator)
            ? current.operator
            : (field?.operators[0] ?? 'equals')

        updateCondition(ruleIndex, conditionIndex, {
            field: fieldKey,
            operator,
            value: '',
        })
    }

    const renderValueInput = (
        ruleIndex: number,
        conditionIndex: number,
        condition: RuleCondition
    ) => {
        const field = fieldMap.get(condition.field)
        if (VALUELESS_OPERATORS.includes(condition.operator)) return null

        const isList = LIST_OPERATORS.includes(condition.operator)

        if (field?.type === 'enum' && field.options?.length && !isList) {
            return (
                <Select
                    value={String(condition.value ?? '')}
                    onValueChange={value =>
                        updateCondition(ruleIndex, conditionIndex, { value })
                    }
                    disabled={disabled}
                >
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select value" />
                    </SelectTrigger>
                    <SelectContent>
                        {field.options.map(option => (
                            <SelectItem key={option} value={option}>
                                {option}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )
        }

        if (field?.type === 'boolean') {
            return (
                <Select
                    value={String(condition.value ?? 'true')}
                    onValueChange={value =>
                        updateCondition(ruleIndex, conditionIndex, {
                            value: value === 'true',
                        })
                    }
                    disabled={disabled}
                >
                    <SelectTrigger className="w-full">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="true">true</SelectItem>
                        <SelectItem value="false">false</SelectItem>
                    </SelectContent>
                </Select>
            )
        }

        return (
            <Input
                value={
                    Array.isArray(condition.value)
                        ? condition.value.join(', ')
                        : String(condition.value ?? '')
                }
                placeholder={
                    isList
                        ? condition.operator === 'between'
                            ? 'low, high'
                            : 'comma separated'
                        : field?.type === 'date'
                          ? 'YYYY-MM-DD'
                          : 'Value'
                }
                type={field?.type === 'date' ? 'date' : 'text'}
                disabled={disabled}
                onChange={event => {
                    const raw = event.target.value
                    updateCondition(ruleIndex, conditionIndex, {
                        value: isList
                            ? raw
                                  .split(',')
                                  .map(part => part.trim())
                                  .filter(Boolean)
                            : raw,
                    })
                }}
            />
        )
    }

    return (
        <div className="space-y-4">
            {rules.length > 1 && (
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">
                        Match users who satisfy
                    </span>
                    <Select
                        value={ruleOperator}
                        onValueChange={value =>
                            onRuleOperatorChange(value as LogicalOperator)
                        }
                        disabled={disabled}
                    >
                        <SelectTrigger className="w-32">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="AND">all rules</SelectItem>
                            <SelectItem value="OR">any rule</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            )}

            {rules.map((rule, ruleIndex) => (
                <div
                    key={ruleIndex}
                    className="space-y-3 rounded-xl border border-border bg-muted/30 p-4"
                >
                    <div className="flex items-center gap-2">
                        <Input
                            value={rule.name ?? ''}
                            placeholder={`Rule ${ruleIndex + 1}`}
                            disabled={disabled}
                            className="h-8 max-w-xs bg-white"
                            onChange={event =>
                                updateRule(ruleIndex, {
                                    name: event.target.value,
                                })
                            }
                        />
                        <Select
                            value={rule.operator}
                            onValueChange={value =>
                                updateRule(ruleIndex, {
                                    operator: value as LogicalOperator,
                                })
                            }
                            disabled={disabled}
                        >
                            <SelectTrigger className="h-8 w-36 bg-white">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="AND">match all</SelectItem>
                                <SelectItem value="OR">match any</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="ml-auto h-8 w-8 text-muted-foreground hover:text-red-600"
                            disabled={disabled}
                            onClick={() =>
                                onChange(
                                    rules.filter((_, i) => i !== ruleIndex)
                                )
                            }
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="space-y-2">
                        {rule.conditions.map((condition, conditionIndex) => {
                            const field = fieldMap.get(condition.field)
                            const operators = field?.operators ?? []

                            return (
                                <div
                                    key={conditionIndex}
                                    className={cn(
                                        'grid gap-2',
                                        VALUELESS_OPERATORS.includes(
                                            condition.operator
                                        )
                                            ? 'grid-cols-[1fr_1fr_auto]'
                                            : 'grid-cols-[1fr_1fr_1fr_auto]'
                                    )}
                                >
                                    <Select
                                        value={condition.field}
                                        onValueChange={value =>
                                            handleFieldChange(
                                                ruleIndex,
                                                conditionIndex,
                                                value
                                            )
                                        }
                                        disabled={disabled}
                                    >
                                        <SelectTrigger className="bg-white">
                                            <SelectValue placeholder="Field" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {fields.map(option => (
                                                <SelectItem
                                                    key={option.field}
                                                    value={option.field}
                                                >
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>

                                    <Select
                                        value={condition.operator}
                                        onValueChange={value =>
                                            updateCondition(
                                                ruleIndex,
                                                conditionIndex,
                                                {
                                                    operator:
                                                        value as ConditionOperator,
                                                }
                                            )
                                        }
                                        disabled={disabled}
                                    >
                                        <SelectTrigger className="bg-white">
                                            <SelectValue placeholder="Operator" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {operators.map(operator => (
                                                <SelectItem
                                                    key={operator}
                                                    value={operator}
                                                >
                                                    {OPERATOR_LABELS[operator]}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>

                                    {renderValueInput(
                                        ruleIndex,
                                        conditionIndex,
                                        condition
                                    )}

                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="text-muted-foreground hover:text-red-600"
                                        disabled={
                                            disabled ||
                                            rule.conditions.length === 1
                                        }
                                        onClick={() =>
                                            updateRule(ruleIndex, {
                                                conditions:
                                                    rule.conditions.filter(
                                                        (_, i) =>
                                                            i !== conditionIndex
                                                    ),
                                            })
                                        }
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            )
                        })}
                    </div>

                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={disabled || !fields.length}
                        onClick={() =>
                            updateRule(ruleIndex, {
                                conditions: [
                                    ...rule.conditions,
                                    emptyCondition(fields),
                                ],
                            })
                        }
                    >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Add condition
                    </Button>
                </div>
            ))}

            <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled || !fields.length}
                onClick={() =>
                    onChange([
                        ...rules,
                        {
                            name: '',
                            conditions: [emptyCondition(fields)],
                            operator: 'AND',
                        },
                    ])
                }
            >
                <Plus className="mr-1 h-3.5 w-3.5" />
                Add rule
            </Button>
        </div>
    )
}
